import React, { useRef } from 'react';
import { Slider, Form, FormInstance, Segmented, InputNumber } from 'antd';

import styles from './index.module.scss';
import { TipType, TipTypeOptions } from '../../../store/statistic';

const valueMap = [0, 1, 2, 3, 4, 5, 10, 15, 20, 30];

interface StepProps {
  form: FormInstance<any>;
  nameValue?: string;
  nameType?: string;
  hideTipType?: boolean;
  valueMin?: number;
  labelMin?: string;
  valueMax?: number;
  labelMax?: string;
}

const SelectTip = ({
  form,
  nameValue = 'tipValue',
  nameType = 'tipType',
  hideTipType = false,
}: StepProps): React.ReactElement => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);

  const tipType: TipType = Form.useWatch(nameType) || '%';
  const tipValue: number = Form.useWatch(nameValue);

  const maxValue = tipType === '%' ? 30 : 9;

  const marksPercent = {
    0: { label: <span className={styles.markLabel}>No Tip</span> },
    5: '5%',
    10: '10%',
    15: '15%',
    18: '18%',
    20: '20%',
    22: '22%',
    25: '25%',
    30: { label: <span className={styles.markLabel}>30+%</span> },
  };

  const marksDollar = {
    0: { label: <span className={styles.markLabel}>No Tip</span> },
    1: '$1',
    2: '$2',
    3: '$3',
    4: '$4',
    5: '$5',
    6: '$10',
    7: '$15',
    8: '$20',
    9: { label: <span className={styles.markLabel}>$20+</span> },
  };

  const handleMove = (clientX: number) => {
    if (!sliderRef.current) return;

    const { left, width } = sliderRef.current.getBoundingClientRect();
    let relativeValue = ((clientX - left) / width) * maxValue;

    if (tipType === '%') {
      const step = 5;

      relativeValue = Math.round(relativeValue / step) * step;
    } else {
      let index = Math.round(
        (relativeValue / maxValue) * (valueMap.length - 1)
      );

      index = Math.max(0, Math.min(valueMap.length - 1, index));
      relativeValue = valueMap[index];
    }

    form.setFieldValue(nameValue, relativeValue);
  };

  const handleMouseDown = () => {
    isDragging.current = true;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging.current) handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = () => {
    isDragging.current = true;
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging.current) handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', handleTouchEnd);
  };

  const getTipClosestIndex = () => {
    let index = 0;
    valueMap.forEach((value, i) => {
      if (tipValue >= value) {
        index = i;
      }
    });
    return index;
  };
  const getTipPosition = () => {
    if (tipType === '%') {
      return `${(tipValue / maxValue) * 100}%`;
    }

    return `${(getTipClosestIndex() / (valueMap.length - 1)) * 100}%`;
  };

  return (
    <>
      {!hideTipType && (
        <div className={styles.wrapperMain}>
          <div style={{ display: 'flex' }}>
            <span style={{ margin: '40px 10px 40px 0' }}>Tip by:</span>
            <Form.Item name={nameType} initialValue={'%'}>
              <Segmented
                options={TipTypeOptions}
                value={tipType}
                onChange={(value) => {
                  form.setFieldValue(nameValue, 5);
                  form.setFieldValue(nameType, value);
                }}
                className={styles.segmented}
                size="large"
              />
            </Form.Item>
          </div>

          {tipType === '$' && (
            <div style={{ display: 'flex' }}>
              <span style={{ marginTop: '5px', marginRight: '10px' }}>
                Custom Amount
              </span>
              <Form.Item name={nameValue}>
                <InputNumber
                  style={{ width: '107px' }}
                  size="large"
                  placeholder="$0"
                />
              </Form.Item>
            </div>
          )}
        </div>
      )}

      <Form.Item name={nameValue} initialValue={5}>
        <div className={styles.wrapper}>
          <div className={styles.sliderWrapper} ref={sliderRef}>
            <Slider
              min={0}
              max={tipType === '%' ? maxValue : valueMap.length - 1}
              step={tipType === '%' ? null : 1}
              marks={tipType === '%' ? marksPercent : marksDollar}
              value={tipType === '%' ? tipValue : getTipClosestIndex()}
              onChange={(value) => {
                const mappedValue = tipType === '%' ? value : valueMap[value];
                form.setFieldValue(nameValue, mappedValue);
              }}
              tooltip={{ formatter: null }}
              className={styles.slider}
            />
            <div
              tabIndex={-1}
              role="button"
              className={styles.selectedTip}
              style={{ left: getTipPosition() }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
            >
              <div className={styles.item}>
                {!tipType
                  ? 0
                  : tipType === '%'
                  ? tipValue > maxValue
                    ? `${tipValue}+%`
                    : `${tipValue}${tipType}`
                  : tipValue > maxValue
                  ? `$${tipValue}+`
                  : `${tipType}${tipValue}`}
              </div>
            </div>
          </div>
        </div>
      </Form.Item>
    </>
  );
};

export default SelectTip;
